<template>
    <footer class="app-footer bg-setting-bg mt-auto min-h-[400px] relative border-t border-white/10 text-white">
        <div class="footer-container">
            <div class="footer-links">
                <div class="link-column">
                    <h2 class="column-title">{{ t('footer.videoTools') }}</h2>
                    <ul class="link-list">
                        <li v-for="tool in videoToolsList" :key="tool.path">
                            <NuxtLink v-if="!tool.isModal" :to="tool.path" class="footer-link"
                                :aria-label="`${t('footer.videoTools')}: ${tool.title}`">
                                {{ tool.title }}
                            </NuxtLink>
                            <button v-else type="button" class="footer-link"
                                :aria-label="`${t('footer.videoTools')}: ${tool.title}`"
                                @click="(e: MouseEvent) => handleToolClick(e, tool)">
                                {{ tool.title }}
                            </button>
                        </li>
                    </ul>
                </div>

                <div class="link-column">
                    <h2 class="column-title">{{ t('footer.imageTools') }}</h2>
                    <ul class="link-list">
                        <li v-for="tool in imageToolsList" :key="tool.path">
                            <NuxtLink v-if="!tool.isModal" :to="tool.path" class="footer-link"
                                :aria-label="`${t('footer.imageTools')}: ${tool.title}`">
                                {{ tool.title }}
                            </NuxtLink>
                            <button v-else type="button" class="footer-link"
                                :aria-label="`${t('footer.imageTools')}: ${tool.title}`"
                                @click="(e: MouseEvent) => handleToolClick(e, tool)">
                                {{ tool.title }}
                            </button>
                        </li>
                    </ul>
                </div>

                <div class="link-column">
                    <h2 class="column-title">{{ t('footer.videoModels') }}</h2>
                    <ul class="link-list">
                        <li v-for="model in videoModelsEnums" :key="model.path">
                            <NuxtLink :to="model.path" class="footer-link"
                                :aria-label="`${t('footer.videoModels')}: ${model.title}`" @click="
                                    handleMenuLinkClick(
                                        {
                                            name: model.title,
                                            path: model.path,
                                            model_key: model.model_key,
                                            model_name: model.model_name,
                                            supportType: model.supportType,
                                            hot: model.is_new,
                                        },
                                        $event
                                    )
                                    ">{{ model.title }}</NuxtLink>
                        </li>
                    </ul>
                </div>

                <div class="link-column">
                    <h2 class="column-title">{{ t('footer.imageModels') }}</h2>
                    <ul class="link-list">
                        <li v-for="model in imageModelsEnums" :key="model.path">
                            <NuxtLink :to="model.path" class="footer-link"
                                :aria-label="`${t('footer.imageModels')}: ${model.title}`" @click="
                                    handleMenuLinkClick(
                                        {
                                            name: model.title,
                                            path: model.path,
                                            model_key: model.model_key,
                                            model_name: model.model_name,
                                            supportType: model.supportType,
                                            hot: model.is_new,
                                        },
                                        $event
                                    )
                                    ">{{ model.title }}</NuxtLink>
                        </li>
                    </ul>
                </div>

                <div class="link-column">
                    <h2 class="column-title">{{ t('footer.aiEffects') }}</h2>
                    <ul class="link-list">
                        <li v-for="tool in aiEffectsEnums" :key="tool.path">
                            <NuxtLink :to="tool.path" class="footer-link"
                                :aria-label="`${t('footer.aiEffects')}: ${tool.title}`">{{ tool.title }}</NuxtLink>
                        </li>
                    </ul>
                </div>

                <div class="link-column">
                    <h2 class="column-title">{{ t('footer.company') }}</h2>
                    <ul class="link-list">
                        <li>
                            <NuxtLink to="/about-us" class="footer-link"
                                :aria-label="`${t('footer.company')}: ${t('footer.aboutUsLink')}`">{{
                                    t('footer.aboutUsLink') }}</NuxtLink>
                        </li>
                        <li>
                            <a href="mailto:service@weryai.com.cn" class="footer-link"
                                :aria-label="`${t('footer.company')}: ${t('footer.contactUs')}`" @click.prevent>
                                <ClientOnly>
                                    <el-popover trigger="hover" width="200">
                                        <template #reference>
                                            <span>{{
                                                t('footer.contactUs')
                                                }}</span>
                                        </template>
                                        <template #default>
                                            <p class="text-center">
                                                service@weryai.com.cn
                                            </p>
                                        </template>
                                    </el-popover>
                                    <template #fallback>
                                        <span>{{ t('footer.contactUs') }}</span>
                                    </template>
                                </ClientOnly>
                            </a>
                        </li>
                        <li>
                            <NuxtLink to="/pricing" class="footer-link"
                                :aria-label="`${t('footer.company')}: ${t('footer.pricingLink')}`">{{
                                    t('footer.pricingLink') }}</NuxtLink>
                        </li>
                        <li>
                            <NuxtLink to="/faq" class="footer-link"
                                :aria-label="`${t('footer.company')}: ${t('footer.faqs')}`">{{
                                    t('footer.faqs') }}</NuxtLink>
                        </li>
                        <li>
                            <NuxtLink to="/refund-policy" class="footer-link"
                                :aria-label="`${t('footer.company')}: ${t('footer.refundPolicy')}`">{{
                                    t('footer.refundPolicy') }}</NuxtLink>
                        </li>
                        <li>
                            <NuxtLink to="/privacy-policy" class="footer-link"
                                :aria-label="`${t('footer.company')}: ${t('footer.privacyPolicy')}`">{{
                                    t('footer.privacyPolicy') }}</NuxtLink>
                        </li>
                        <li>
                            <NuxtLink to="/terms-and-conditions" class="footer-link"
                                :aria-label="`${t('footer.company')}: ${t('footer.termsAndConditions')}`">{{
                                    t('footer.termsAndConditions') }}
                            </NuxtLink>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- 社交媒体图标 -->
        <div v-if="false" class="social-section pr-5 ">
            <div class="social-icons">
                <a href="tel:+6594687562" class="social-icon" aria-label="Phone">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                </a>
                <a href="mailto:service@weryai.com.cn" class="social-icon" aria-label="Email">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                </a>

                <a href="https://twitter.com/weryai" class="social-icon" aria-label="Twitter" target="_blank"
                    rel="noopener noreferrer">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                        <path
                            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                </a>
                <button type="button" class="social-icon" aria-label="微鲤AI Discord" @click="getUrl()">
                    <span class="i-nav-discord-icon navIcon"></span>
                </button>
                <a href="https://www.instagram.com/weryai_official/" target="_blank" class="social-icon"
                    rel="noopener noreferrer" aria-label="微鲤AI Instagram">
                    <span class="i-nav-instagram-icon navIcon"></span>
                </a>
                <a href="https://www.tiktok.com/@weryai_" target="_blank" class="social-icon" rel="noopener noreferrer"
                    aria-label="微鲤AI TikTok">
                    <span class="i-nav-tiktok-icon navIcon"></span>
                </a>
            </div>
        </div>
        <p class="w-full flex gap-2 items-center justify-center text-text-adad text-sm py-4">
            <span> {{ t('footer.copyright') }}</span>
            <span>|</span>
            <a class="text-text-adad text-sm my-2 cursor-pointer hover:underline" target="_blank"
                href="https://beian.miit.gov.cn/">
                琼ICP备2026003673号-6
            </a>
        </p>
    </footer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from '~/composables/useI18n'
import { useToolsHooks } from '~/utils/useHooks'
import { emitOpenPromptReversalModal } from '~/utils/emitter'
import { getCountryInfo } from '~/utils/log-event-func'
import { getUserThirdPartyBindInfoApi } from '@/api/social-media'

// 使用国际化功能
const { t, locale, changeLanguage } = useI18n()

// 使用工具钩子
const {
    handleMenuLinkClick,
    videoModelsEnums,
    imageModelsEnums,
    aiVideosToolsList,
    aiImageToolsEnums,
} = useToolsHooks()

// 视频工具列表
const videoToolsList: any = computed(() => {
    const origin_ = [
        {
            path: '/image-to-video',
            title: t('footer.imageToVideoAI'),
        },
        {
            path: '/text-to-video',
            title: t('footer.textToVideoAI'),
        },
        {
            path: '/video-to-video',
            title: t('footer.aiVideoEditor'),
        },
    ]
    return origin_.concat(aiVideosToolsList.value)
})

// AI 特效
const aiEffectsEnums = computed(() => [
    {
        path: '/video-effects',
        title: t('menus.videoEffects'),
    },
    {
        path: '/photo-effects',
        title: t('menus.photoEffects'),
    },
])

// 图像工具列表
const imageToolsList: any = computed(() => {
    return [
        {
            path: '/text-to-image',
            title: t('footer.aiImageGenerator'),
        },
        {
            path: '/image-to-image',
            title: t('footer.imageToImageAI'),
        },
    ].concat(aiImageToolsEnums.value)
})

/**
 * 处理工具点击
 */
const handleToolClick = (e: MouseEvent, item: any) => {
    if (item.isModal) {
        e.preventDefault()
        emitOpenPromptReversalModal()
    }
}

const beian_content = ref<string>('')

const route = useRoute()

// 计算是否为中文路径
const is_zh = computed(() => {
    return !!route.path?.includes('/zh')
})

const discordLoading = ref(false)

// 获取链接地址
const getUrl = async () => {
    if (discordLoading.value) return
    discordLoading.value = true
    try {
        const res: any = await getUserThirdPartyBindInfoApi()
        console.log('响应结果', res)
        if (res.status !== 200) {
            ElMessage.warning(res.desc)
        } else {
            const discord_id = res.data?.discord_id
            if (discord_id != 0) {
                ElMessage.info(t('menus.discordBound'))
                window.open('https://discord.gg/xTY7vmwarm', '_blank')
            } else {
                const navUrl = res.data?.discord_auth_url
                if (navUrl) {
                    window.open(navUrl, '_blank')
                }
            }
        }
    } catch (err) {
        console.error('getUrl error:', err)
    } finally {
        discordLoading.value = false
    }
}
</script>

<style lang="less" scoped>
.app-footer {
    color: white;
    margin-top: auto;
    position: relative;
    background-color: #0d0e12;
    border-top: 1px solid rgba(229, 231, 235, 0.1);
    min-height: 400px;
}

.footer-container {
    padding: 56px;
    gap: 3rem;
    align-items: start;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    margin: 0 52px;
}

/* 品牌区域 */
.brand-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.brand-logo {
    margin-bottom: 0.5rem;
}

.logo-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    color: white;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
}

.logo-image {
    height: 32px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
}

.brand-tagline {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.5;
    margin: 0;
}

.copyright {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
}

.language-selector {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    min-width: 120px;
    position: relative;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
    }
}

.dropdown-arrow {
    transition: transform 0.2s ease;

    &.rotate-180 {
        transform: rotate(180deg);
    }
}

.language-dropdown {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    background: rgba(15, 23, 42, 0.98);
    backdrop-filter: blur(20px);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 -10px 25px rgba(0, 0, 0, 0.3);
    z-index: 1000;
    margin-bottom: 0.25rem;
    max-height: 300px;
    overflow-y: auto;
    min-width: 300px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
}

.language-option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: white;
    border-radius: 0;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    &.active {
        background: rgba(59, 130, 246, 0.2);
        color: #60a5fa;
    }

    /* 左上角 */
    &:nth-child(1) {
        border-radius: 8px 0 0 0;
    }

    /* 右上角 */
    &:nth-child(2) {
        border-radius: 0 8px 0 0;
    }

    /* 左下角 */
    &:nth-last-child(2) {
        border-radius: 0 0 0 8px;
    }

    /* 右下角 */
    &:nth-last-child(1) {
        border-radius: 0 0 8px 0;
    }
}

.flag {
    font-size: 16px;
    flex-shrink: 0;
}

.language-name {
    font-size: 14px;
    font-weight: 500;
}

.globe-icon {
    color: rgba(255, 255, 255, 0.7);
}

.language-text {
    font-size: 14px;
    color: white;
}

.dropdown-arrow {
    color: rgba(255, 255, 255, 0.7);
}

/* 页脚链接区域 */
.footer-links {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 2rem;
}

.link-column {
    display: flex;
    flex-direction: column;
}

.column-title {
    font-size: 14px;
    font-weight: 600;
    color: #f5f2f0;
    margin: 0 0 20px 0;
}

.link-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.footer-link {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 4px 0;
    color: #adadad;
    text-decoration: none;
    font-size: 14px;
    font-weight: 400;
    background: transparent;
    border: 0;
    cursor: pointer;
    appearance: none;
    transition: color 0.2s ease;

    &:hover {
        color: var(--weryai-text-active);
        text-decoration: underline;
    }
}

/* 社交媒体区域 */
.social-section {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    margin-top: 2rem;
}

.social-icons {
    display: flex;
    gap: 1rem;
}

.social-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    appearance: none;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
        color: white;
        transform: translateY(-2px);
    }
}

.social-icons .navIcon {
    width: 20px;
    height: 20px;
    display: inline-block;
    -webkit-mask-size: contain;
    background-image: none;
    background-color: currentColor;
    mask-size: contain;
}

.i-nav-twitter-icon {
    mask: url('~/assets/images/icons/twitter.svg') no-repeat center;
    -webkit-mask: url('~/assets/images/icons/twitter.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask-size: contain;
}

.i-nav-discord-icon {
    mask: url('~/assets/images/icons/discord.svg') no-repeat center;
    -webkit-mask: url('~/assets/images/icons/discord.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask-size: contain;
}

.i-nav-instagram-icon {
    mask: url('~/assets/images/icons/instagram.svg') no-repeat center;
    -webkit-mask: url('~/assets/images/icons/instagram.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask-size: contain;
}

.i-nav-tiktok-icon {
    mask: url('~/assets/images/icons/tiktok.svg') no-repeat center;
    -webkit-mask: url('~/assets/images/icons/tiktok.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask-size: contain;
}

/* 响应式设计 */
@media (max-width: 1024px) {
    .footer-container {
        grid-template-columns: 1fr;
        gap: 2rem;
    }

    .footer-links {
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5rem;
    }
}

@media (max-width: 768px) {
    .footer-container {
        padding: 1.5rem;
        gap: 1.5rem;
    }

    .footer-links {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
    }

    .brand-section {
        text-align: center;
    }

    .language-selector {
        margin: 0 auto;
        min-width: 140px;
    }

    .language-dropdown {
        left: 50%;
        transform: translateX(-50%);
        width: 280px;
        min-width: 280px;
        bottom: 100%;
        margin-bottom: 0.25rem;
    }

    .social-section {
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .footer-links {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .link-column {
        text-align: center;
    }

    .column-title {
        font-size: 15px;
        margin-bottom: 0.75rem;
    }

    .footer-link {
        font-size: 13px;
    }

    .brand-tagline {
        font-size: 13px;
    }
}
</style>
